/**
 * React Query Setup
 * 
 * This file provides the QueryClient configuration and provider setup
 * for React Query in the application.
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Create and configure the QueryClient
 * 
 * Default settings:
 * - Retry failed queries 3 times
 * - Stale time: 5 minutes (data is considered fresh for 5 minutes)
 * - Cache time: 10 minutes (unused data is garbage collected after 10 minutes)
 * - Refetch on window focus (disabled by default)
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 3,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
            refetchOnWindowFocus: false, // Disable refetch on window focus
            refetchOnReconnect: true, // Refetch on reconnect
            refetchOnMount: true, // Refetch on mount if data is stale
        },
        mutations: {
            retry: false, // Never retry mutations - they are not idempotent
        },
    },
});

/**
 * Query Keys Factory
 * Centralized query keys for better organization and type safety
 */
export const queryKeys = {
    pascos: {
        all: ['pascos'] as const,
        lists: () => [...queryKeys.pascos.all, 'list'] as const,
        list: (filters?: Record<string, unknown>) =>
            [...queryKeys.pascos.lists(), { filters }] as const,
        details: () => [...queryKeys.pascos.all, 'detail'] as const,
        detail: (id: string) => [...queryKeys.pascos.details(), id] as const,
    },
    tasks: {
        all: ['tasks'] as const,
        lists: () => [...queryKeys.tasks.all, 'list'] as const,
        list: (filters?: Record<string, unknown>) =>
            [...queryKeys.tasks.lists(), { filters }] as const,
        details: () => [...queryKeys.tasks.all, 'detail'] as const,
        detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
    },
} as const;
